// Single helper that emits the new MN-4800 events
// (`transfer_upload_validation_friction`, `transfer_upload_failed`)
// alongside the legacy `image_upload_*` events. Importers wire this in at
// every legacy emission site so the "parallel-emission window" stays
// consistent across emitters and the AC's per-upload_uuid exclusivity
// invariant holds for the whole client.
//
// Pure orchestration — does NOT replace legacy emits. The caller still owns
// the legacy `submitDtfEvent` / `trackRudderstackImageUploadError` calls.
//
// MN-4800 SUNSET: parallel emission is scheduled to retire on 2026-07-18
// (https://dekeo.atlassian.net/browse/MN-4800). After that date, dashboards
// must already be migrated to the new events; the legacy `image_upload_*`
// emits + this dual-emit helper should be removed in a follow-up PR. Mixpanel
// projects bill per row × ingestion, so each month past target is real spend.

import { submitDtfEvent } from "common/utils/mixpanel/submit-dtf-events";
import { TRANSFERS_GROUP } from "common/utils/app-registry/contants";
import { generateUUID } from "common/utils/uuid";
import { classifyTransferUploadError } from "./classifier";
import { getUploadUuid } from "./upload-uuid-tracker";
import { sanitizeErrorMessage, sanitizeFileName } from "./sanitize-pii";

export const TRANSFER_FRICTION_EVENT = "transfer_upload_validation_friction";
export const TRANSFER_FAILED_EVENT = "transfer_upload_failed";

// Transfer-pipeline business lines whose error events are in scope for the
// MN-4800 split. Includes DTF, Stickers, Spangles, and Digital Assets — all
// share the same dropzone/dimension validators. Print runs through a
// separate code path (frontend/common/utils/print/tracker.js) and must not
// emit transfer events.
const TRANSFER_BUSINESS_LINES = new Set(TRANSFERS_GROUP);

export const isTransferBusinessLine = businessLine =>
  typeof businessLine === "string" && TRANSFER_BUSINESS_LINES.has(businessLine);

const buildBasePayload = ({ errorContext, file, pageInfo }) => {
  const fileName = errorContext?.file_name ?? file?.name;
  const fileType = errorContext?.file_type ?? file?.type;
  const fileSize = errorContext?.file_size ?? file?.size;
  const errorType = errorContext?.error_type;
  const errorMessage = errorContext?.error_message;
  const httpStatusCode = errorContext?.http_status_code;
  const realUploadUuid = errorContext?.upload_uuid ?? getUploadUuid(file);
  const businessLine = errorContext?.business_line ?? pageInfo?.businessLine;
  const page = errorContext?.page ?? pageInfo?.pageValue;

  // MN-4800: when a real upload_uuid isn't available (every current emitter
  // threads one, but a future caller could regress), synthesize a fresh uuid
  // and tag the payload so dashboards can exclude the synthetic bucket from
  // join queries with image_upload_started. Emitting `undefined` would
  // silently collapse all such rows into one bucket and break
  // count(distinct upload_uuid) metrics.
  const uploadUuid = realUploadUuid || `synthetic-${generateUUID()}`;
  const isSynthetic = !realUploadUuid;

  // MN-4800: PII sanitization for the new event payloads only. Legacy
  // image_upload_* dashboards already query raw values; rewriting those
  // mid-flight would break running queries. Apply scrubbing to the new
  // events so they comply with GDPR data-minimization from day one.
  return {
    upload_uuid: uploadUuid,
    upload_uuid_synthetic: isSynthetic,
    error_type: errorType,
    error_message: sanitizeErrorMessage(errorMessage),
    business_line: businessLine,
    page,
    file_name: sanitizeFileName(fileName),
    file_type: fileType,
    file_size: fileSize,
    http_status_code: httpStatusCode
  };
};

// Emits exactly one of `transfer_upload_validation_friction` or
// `transfer_upload_failed` based on classifyTransferUploadError. No-op when
// the business line is outside the transfer pipeline so non-transfer flows
// (Print) don't pollute transfer dashboards.
//
// @param {object} args
// @param {string} args.legacyEvent      legacy event name about to be emitted
// @param {object} args.errorContext     getErrorContext() result
// @param {object} [args.file]           file object (for fallback fields)
// @param {object} [args.pageInfo]       pageInfo (for fallback fields)
// @returns {{event: string, payload: object} | null}
//   The emitted event descriptor, or null if no event was emitted.
// MN-4800: a dev-only sentry that catches the case where a transfer-pipeline
// caller passes a non-canonical businessLine label (e.g. "DTF" or lowercase
// "transfers" instead of "Transfers"). In production we silently no-op to
// avoid console spam and accidental analytics-pipeline noise; in
// dev/staging we warn so capitalization drift is visible during QA.
const isProductionEnv = () => {
  try {
    // eslint-disable-next-line no-undef
    return typeof process !== "undefined" && process.env && process.env.NODE_ENV === "production";
  } catch {
    return true;
  }
};

const warnIfSuspectGateMiss = businessLine => {
  if (isProductionEnv()) return;
  // Only warn for non-empty strings — undefined/null are deliberate "no
  // business line" signals from non-transfer callers (e.g. Print) and not
  // worth flagging.
  if (typeof businessLine !== "string" || businessLine === "") return;
  // eslint-disable-next-line no-console
  console.warn(
    `[MN-4800] transfer_upload_* parallel emit gated out for businessLine=${JSON.stringify(
      businessLine
    )}. Expected one of TRANSFERS_GROUP. Likely capitalization drift.`
  );
};

export const emitTransferUploadParallelEvent = ({
  legacyEvent,
  errorContext,
  file,
  pageInfo
} = {}) => {
  const businessLine = errorContext?.business_line ?? pageInfo?.businessLine;
  if (!isTransferBusinessLine(businessLine)) {
    warnIfSuspectGateMiss(businessLine);
    return null;
  }

  const classification = classifyTransferUploadError({
    errorEvent: legacyEvent,
    errorType: errorContext?.error_type,
    errorMessage: errorContext?.error_message
  });

  const base = buildBasePayload({ errorContext, file, pageInfo });

  if (classification.kind === "validation_friction") {
    const payload = {
      ...base,
      customer_correction_hint: classification.customer_correction_hint
    };
    submitDtfEvent(TRANSFER_FRICTION_EVENT, payload);
    return { event: TRANSFER_FRICTION_EVENT, payload };
  }

  const payload = { ...base, failure_stage: classification.failure_stage };
  submitDtfEvent(TRANSFER_FAILED_EVENT, payload);
  return { event: TRANSFER_FAILED_EVENT, payload };
};
